import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { api } from '../lib/api';
import { Alert, Field } from '../components/ui';
import { GoogleButton } from '../public/GoogleButton';
import { AuthShell } from '../public/PublicShell';

export function Login({ onAuthenticated }: { onAuthenticated: () => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Hook at the top level, not inside the expression below -- a hook called from
  // a nested function is the kind of thing that works until someone makes the
  // surrounding code conditional.
  const { search } = useLocation();

  // The Google callback redirects here with a fixed reason code when it cannot
  // complete. Mapped to text locally rather than printed from the query string:
  // rendering an arbitrary query parameter is how a login page ends up
  // displaying an attacker's message.
  const reason = new URLSearchParams(search).get('error');
  const callbackError =
    reason === 'google_cancelled'
      ? 'Google sign-in was cancelled.'
      : reason === 'google_failed'
        ? 'Google sign-in could not be completed. Try again, or use your password.'
        : null;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.auth.login(email, password);
      await onAuthenticated();
    } catch (err) {
      // The API answers the same way for an unknown account and a wrong
      // password, on purpose -- so this message is deliberately vague too.
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell crossLink={{ to: '/signup', label: "Don't have an account? Sign up" }}>
      <h1>Log in</h1>
      <p className="auth-sub">Welcome back — send some email.</p>

      {callbackError && <Alert kind="error">{callbackError}</Alert>}

      <GoogleButton label="Continue with Google" />

      <form onSubmit={submit}>
        {error && <Alert kind="error">{error}</Alert>}

        {/* No per-field errors on this form, deliberately. A sign-in failure must
            not say which of the two was wrong: "no account with that email" is an
            account-enumeration oracle, and "wrong password" confirms the address
            exists. The single form-level message below is the correct trade. */}
        <Field id="email" label="Email">
          {(props) => (
            <input
              {...props}
              type="email"
              autoComplete="username"
              placeholder="you@company.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>

        <Field id="password" label="Password">
          {(props) => (
            <input
              {...props}
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>

        <button className="btn-primary btn-block" disabled={busy}>
          {busy ? 'Signing in…' : 'Log in'}
        </button>
      </form>

      <p className="switch">
        New here? <Link to="/signup">Create an account</Link>
      </p>
    </AuthShell>
  );
}
